import { UUID_PATTERN } from "@p0security/integrations-entra-id-shared";
import {
  Element,
  InstallSpec,
  ItemComponent,
} from "@p0security/shared/install/types";
import { SECONDS } from "@p0security/util/time";
import { isa } from "@p0security/util/types/is";
import { isSafe as isSafeRegex } from "redos-detector";

import { CATALOG_KEY_FORMAT, parseCatalogKey } from "./catalog-key.ts";
import {
  DATABRICKS_CONNECTOR_FUNCTION_NAME,
  DatabricksAccountsUrls,
  DatabricksWorkspaceDomains,
} from "./constants.ts";

/**
 * Refuses a value that isn't a UUID, naming what it is (`label`). The
 * connector parses account and application IDs with zod's `guid()`, which
 * takes the same shape.
 */
const uuidValidator = (label: string) => async (_id: string, value: string) => {
  if (!UUID_PATTERN.test(value))
    return `${label} are UUIDs, e.g. 01234567-89ab-cdef-0123-456789abcdef`;
};

// `URL.parse` would do this, but the frontend bundles this file, and its
// browser targets (`build.target` in frontend/vite.config.mjs) predate it.
const toUrl = (value: string) => {
  try {
    return new URL(value);
  } catch {
    return undefined;
  }
};

/** A Databricks workspace ID, which is all digits. The connector refuses anything else. */
const WORKSPACE_ID_PATTERN = /^\d+$/;

const WORKSPACE_DOMAINS = Object.values(DatabricksWorkspaceDomains);

/**
 * The workspace domain of the account console at `accountsUrl`, or
 * `undefined` if it isn't one. An accounts URL may end in a slash.
 */
export const workspaceDomainOf = (accountsUrl: string) => {
  const origin = toUrl(accountsUrl)?.origin;
  return origin && isa(DatabricksAccountsUrls, origin)
    ? DatabricksWorkspaceDomains[origin]
    : undefined;
};

/**
 * A workspace URL in the one form P0 saves: `https://`, then a lowercase host
 * on a workspace domain, and nothing after it. It is the Terraform provider's
 * pattern (`workspaceUrlRegex` in terraform-provider-p0), so the URL P0 saves
 * always reads back as it was configured. The connector also takes a trailing
 * slash or an uppercase host, which it normalizes.
 */
const WORKSPACE_URL_PATTERN = new RegExp(
  // Built from the domain constants, with their dots escaped, and never from
  // input.
  // nosemgrep: javascript.lang.security.audit.detect-non-literal-regexp.detect-non-literal-regexp
  `^https://(?:[a-z0-9-]+\\.)+(?:${WORKSPACE_DOMAINS.map((domain) => domain.replace(/\./g, "\\.")).join("|")})$`
);

export const WORKSPACE_URL_ERROR = `The workspace URL must be https://<host> on a Databricks workspace domain: ${WORKSPACE_DOMAINS.slice(0, -1).join(", ")} or ${WORKSPACE_DOMAINS[WORKSPACE_DOMAINS.length - 1]}. Enter the host in lowercase, with nothing after it, not even a trailing slash. You can't change the URL once the workspace is added.`;

const UC_NAME_MAX_LENGTH = 255;

/** Besides whitespace and control characters, what Unity Catalog forbids in a name. */
const UC_FORBIDDEN_CHARACTERS = "./";

/** What P0's install API can't carry in its URL paths, which hold item keys unescaped. */
const URL_PATH_FORBIDDEN_CHARACTERS = "#?%\\";

const containsAny = (name: string, characters: string) =>
  [...name].some((char) => characters.includes(char));

const containsControlCharacter = (name: string) =>
  [...name].some((char) => char.charCodeAt(0) < 0x20 || char === "\x7f");

/**
 * Why `name` can't be a catalog's name, if it can't, following Unity Catalog's
 * rules (https://docs.databricks.com/aws/en/sql/language-manual/sql-ref-names).
 * The period it forbids is also what the connector splits securable names on.
 */
export const catalogNameError = (name: string) => {
  if (!name) return "Enter the catalog's name";
  if (name.length > UC_NAME_MAX_LENGTH)
    return `Catalog names are at most ${UC_NAME_MAX_LENGTH} characters`;
  if (
    containsAny(name, UC_FORBIDDEN_CHARACTERS) ||
    // Any whitespace, as `parseCatalogKey` refuses, not just a space.
    /\s/.test(name) ||
    containsControlCharacter(name)
  )
    return "Catalog names can't contain a period, a space, a forward slash or a control character";
  if (name !== name.toLowerCase())
    return `Unity Catalog stores catalog names in lowercase, so enter ${name.toLowerCase()}`;
  if (containsAny(name, URL_PATH_FORBIDDEN_CHARACTERS))
    return "P0 can't install a catalog whose name contains #, ?, % or \\. Contact support@p0.dev if you need P0 to manage it.";
};

/**
 * The longest domain pattern the validator checks. redos-detector's `timeout`
 * doesn't cover reading a pattern in, which takes seconds for one of tens of
 * kilobytes.
 */
const MAX_DOMAIN_PATTERN_LENGTH = 1000;

/**
 * How long redos-detector may take to check one pattern. It has no limit by
 * default, and takes over 20 seconds on `a{100000}`. The validator runs in
 * the form on every keystroke, and in the API on every save.
 */
const DOMAIN_PATTERN_CHECK_TIMEOUT = 1 * SECONDS;

/**
 * `pattern` as the connector compiles it, anchored to the whole domain, or
 * `undefined` if it doesn't compile.
 */
const compileDomainPattern = (pattern: string) => {
  try {
    // Only compiled and inspected, never run, so a pattern that would
    // backtrack badly can't hang the form. The connector is what matches it.
    // nosemgrep: javascript.lang.security.audit.detect-non-literal-regexp.detect-non-literal-regexp
    return new RegExp(`^(?:${pattern})$`);
  } catch {
    return undefined;
  }
};

/**
 * Whether redos-detector can show that matching `domainPattern` never
 * backtracks catastrophically. The connector matches it against a domain on
 * every request, so one that can would tie up its Lambda.
 *
 * A pattern redos-detector can't read, such as one with a named group, or
 * can't finish checking in time, isn't shown to be safe.
 *
 * The check is case-sensitive, because redos-detector takes no `i` flag, and
 * the connector matches with one. So it misses a pattern that is only unsafe
 * across cases, such as `(a|A)+`.
 */
const isSafeToMatch = (domainPattern: RegExp) => {
  try {
    return isSafeRegex(domainPattern, {
      timeout: DOMAIN_PATTERN_CHECK_TIMEOUT,
    }).safe;
  } catch {
    return false;
  }
};

/**
 * One install, four rungs, each pointing at the rung above: `connector`
 * (whose key is the `aws` item) → `account` → `workspace` → `catalog`. An
 * account or workspace points with a `dynamic` element, which the customer
 * chooses, and a catalog with a `hidden` one, which the backend takes from
 * the catalog's key. Each item owns exactly one artifact at its own
 * granularity, so installing never has to ask whether something exists.
 *
 * The item keys are external identifiers: an AWS account ID, a Databricks
 * account ID (a UUID), a workspace ID, and `<catalog>@<workspace ID>` (see
 * `catalog-key.ts`). A chosen pointer's options
 * come from `integrationOptionProvider`, which hides any candidate whose key
 * is also a key of the component being installed, so each component's keys
 * must not coincide with those of the component it points at.
 */
export const DatabricksComponents = InstallSpec({
  connector: ItemComponent({
    label: "Connector",
    description: `The ${DATABRICKS_CONNECTOR_FUNCTION_NAME} Lambda that P0 invokes to manage Databricks, one per AWS account. Its key is the ID of an AWS account installed in P0's AWS integration. Every Databricks account below is reached through it.`,
    type: "dynamic",
    maturity: "beta",
    schema: {
      region: Element({
        label: "Region",
        description: "The AWS region the connector's Lambda runs in",
        type: "dynamic",
        step: "new",
      }),
      domainPattern: Element({
        label: "Allowed email domains",
        description:
          "A regular expression the connector matches against the whole email domain of every user it grants to, e.g. example\\.com. It refuses any other user.",
        type: "string",
        step: "new",
        validator: async (_id, pattern) => {
          if (!pattern?.trim()) return "Allowed email domains are required";
          if (pattern.length > MAX_DOMAIN_PATTERN_LENGTH)
            return `Allowed email domains must be at most ${MAX_DOMAIN_PATTERN_LENGTH} characters`;
          const compiled = compileDomainPattern(pattern);
          if (!compiled)
            return "Allowed email domains must be a valid regular expression";
          if (!isSafeToMatch(compiled))
            return "Allowed email domains must be a regular expression that P0 can verify won't backtrack catastrophically. Simplify its repetition, e.g. allow subdomains with (.+\\.)?example\\.com rather than (.+\\.)*example\\.com.";
        },
      }),
    },
  }),
  account: ItemComponent({
    label: "Account",
    description:
      "One Databricks account, which P0 manages through a service principal there that the connector federates into. The principal holds account admin only while account-admin requests are turned on. Its key is the Databricks account ID.",
    type: "string",
    maturity: "beta",
    validator: uuidValidator("Databricks account IDs"),
    schema: {
      connector: Element({
        label: "Connector",
        description: "The installed connector that reaches this account",
        type: "dynamic",
        step: "new",
      }),
      accountsUrl: Element({
        label: "Accounts URL",
        description:
          "The URL of this account's console, e.g. https://accounts.cloud.databricks.com",
        type: "string",
        step: "new",
        validator: async (_id, value) => {
          const url = toUrl(value);
          const isBareOrigin =
            !!url &&
            url.pathname === "/" &&
            !url.search &&
            !url.hash &&
            !url.username &&
            !url.password;
          if (!isBareOrigin || !isa(DatabricksAccountsUrls, url.origin))
            return `The accounts URL must be one of ${DatabricksAccountsUrls.join(", ")}`;
        },
      }),
      // An input, not a discovery: the connector needs it to authenticate at
      // all, as the client ID of its federated token exchange.
      applicationId: Element({
        label: "Service principal application ID",
        description:
          "The application ID of the service principal this account's terraform creates, from its output. The connector authenticates to Databricks as this principal.",
        type: "string",
        step: "configure",
        validator: uuidValidator("Application IDs"),
      }),
      isAccountAdminEnabled: Element({
        label: "Enable account-admin requests",
        description:
          "Lets users request account admin in this account. P0's service principal needs account admin itself to grant it. When you turn this on, P0 shows the Terraform that gives it the role. Let open account-admin requests end before you turn this off, since P0 needs the role to revoke them.",
        type: "switch",
        optional: true,
        step: "configure",
      }),
    },
  }),
  workspace: ItemComponent({
    label: "Workspace",
    description:
      "One workspace in an installed account, which P0 reaches at its URL. The account's service principal is assigned to it as a user, or as a workspace admin while workspace-admin requests are turned on. Its key is the Databricks workspace ID, the number after ?o= in the workspace's URL when you open it in a browser.",
    // Typed rather than chosen: listing an account's workspaces takes account
    // admin, which the service principal holds only for account-admin
    // requests.
    type: "string",
    maturity: "beta",
    validator: async (_id, key) => {
      if (!WORKSPACE_ID_PATTERN.test(key))
        return "Databricks workspace IDs are numbers, e.g. 1234567890123456. When you open the workspace in a browser, its ID is the number after ?o= in the URL.";
    },
    schema: {
      account: Element({
        label: "Account",
        description: "The installed Databricks account this workspace is in",
        type: "dynamic",
        step: "new",
      }),
      workspaceUrl: Element({
        label: "Workspace URL",
        description:
          "The workspace's URL, e.g. https://dbc-1234abcd-5678.cloud.databricks.com. P0 reaches the workspace there, and checks that it belongs to this workspace ID.",
        type: "string",
        step: "new",
        validator: async (_id, value) => {
          if (!WORKSPACE_URL_PATTERN.test(value)) return WORKSPACE_URL_ERROR;
        },
      }),
      workspaceName: Element({
        label: "Workspace name",
        description:
          "A name for the workspace in P0, e.g. analytics-prod. Requests show it, and approval rules can match it with the workspace filter's name key.",
        type: "string",
        step: "configure",
      }),
      isWorkspaceAdminEnabled: Element({
        label: "Enable workspace-admin requests",
        description:
          "Lets users request workspace admin on this workspace. P0's service principal needs to be a workspace admin itself to grant it. When you turn this on, P0 shows the Terraform that makes it one. Let open workspace-admin requests on this workspace end before you turn this off, since P0 needs the role to revoke them.",
        type: "switch",
        optional: true,
        step: "configure",
      }),
    },
  }),
  catalog: ItemComponent({
    label: "Catalog",
    description: `One Unity Catalog catalog on which the account's service principal holds MANAGE, so that P0 can grant privileges in it. Its key is the catalog name and the ID of the workspace P0 reaches it through, as ${CATALOG_KEY_FORMAT}, because catalog names are unique only within a metastore.`,
    // Typed rather than chosen: Unity Catalog lists only the catalogs the
    // principal already has privileges on, and it has none on a catalog until
    // this item's terraform grants MANAGE.
    type: "string",
    maturity: "beta",
    validator: async (_id, key) => {
      const parsed = parseCatalogKey(key);
      if (!parsed)
        return `Catalog keys are ${CATALOG_KEY_FORMAT}, e.g. main@1234567890123456`;
      return catalogNameError(parsed.catalog);
    },
    schema: {
      // The key already names the workspace, so the customer isn't asked for
      // it again: the backend's assembler saves the key's, and `step: "new"`
      // refuses a change to it afterwards. `hidden` rather than `generated`,
      // which the installer's form would show, empty, on the new-item step,
      // and after that would only repeat the ID in the key.
      workspace: Element({
        label: "Workspace",
        description:
          "The installed workspace P0 reaches this catalog through, the one its key names",
        type: "hidden",
        step: "new",
      }),
    },
  }),
});
